import { useObserver } from "mobx-react-lite";
import { useCallback, useMemo } from "react";

import {
  ThreadSessionStore,
  threadSessionManager,
} from "@/store/threadSession";
import { ThreadExportProgressSnapshot } from "../store/helpers/threadSessionStorage";
import { RtcTask } from "../store/thread";

interface ExportProgressActions {
  markExportProgress: (rtcTask: RtcTask) => Promise<void>;
  markDownloaded: (shouldDismiss: boolean) => Promise<void>;
}

interface UseThreadExportProgressResult {
  exportProgress: ThreadExportProgressSnapshot;
  actions: ExportProgressActions;
}

export function useThreadExportProgress(
  threadId: string
): UseThreadExportProgressResult {
  const sessionStore: ThreadSessionStore = useMemo(() => {
    return threadSessionManager.get(threadId);
  }, [threadId]);

  const markExportProgress = useCallback(
    (task: RtcTask) => {
      return sessionStore.markExportProgress(task);
    },
    [sessionStore]
  );

  const markDownloaded = useCallback(
    (shouldDismiss: boolean) => {
      return sessionStore.markExportDownloaded(shouldDismiss);
    },
    [sessionStore]
  );

  const actions = useMemo(
    () => ({
      markExportProgress,
      markDownloaded,
    }),
    [markDownloaded, markExportProgress]
  );

  const exportProgress = useObserver(() => sessionStore.exportProgress);

  return {
    exportProgress,
    actions,
  };
}
